import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardText, WifiHigh, WifiSlash, Key, Warning, CurrencyJpy, Printer, Rows, Pulse, Plugs, ArrowUp, ArrowDown, CircleDashed } from '@phosphor-icons/react';
import { ResponsiveContainer, AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip as ChartTooltip } from 'recharts';
import { useAuth, canEditIt } from '@/auth/AuthContext';
import { PageHeader } from '@/components/enterprise/PageHeader';
import { DashboardZone, ActionTile, AllClear, MetricsDisclosure } from '@/components/enterprise/DashboardZone';
import { itApi } from './itApi';
import { openPrintWindow } from '@/lib/printWindow';
import { buildItBudgetReportHtml } from './itBudgetReport';
import { formatDateTime } from '@/utils/datetime';

const yenFmt = (n) => (typeof n === 'number' ? `¥${Math.round(n).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })}` : '—');

const fmtBytes = (n) => {
  if (!n || n <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0; let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v >= 100 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
};

const fmtSince = (m) => {
  if (m == null) return '不明';
  if (m < 60) return `${m}分`;
  const h = Math.floor(m / 60);
  if (h < 48) return m % 60 ? `${h}時間${m % 60}分` : `${h}時間`;
  return `${Math.floor(h / 24)}日`;
};


export default function ITDashboard() {
  const { user } = useAuth();
  const admin = canEditIt(user);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [omada, setOmada] = useState(null);
  const [renewals, setRenewals] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [assetStats, setAssetStats] = useState(null);
  const [assets, setAssets] = useState([]);
  const [fullRacks, setFullRacks] = useState([]);
  const [health, setHealth] = useState(null);
  const [healthHist, setHealthHist] = useState([]);
  const [err, setErr] = useState(false);

  useEffect(() => {
    itApi.stats().then(setStats).catch(() => setErr(true));
    if (admin) {
      itApi.omadaStatus().then(setOmada).catch(() => {});
      itApi.assetStats().then(setAssetStats).catch(() => {});
      itApi.assets().then(setAssets).catch(() => {});
      itApi.rackUsage().then((rows) => setFullRacks(rows.filter((r) => r.usage_pct >= 90))).catch(() => {});
      itApi.omadaHealth().then(setHealth).catch(() => {});
      itApi.omadaHealthHistory(30).then(setHealthHist).catch(() => {});
      itApi.licenses().then((rows) => {
        setLicenses(rows);
        const hot = rows.filter((l) => l.expired || l.expiring_soon);
        hot.sort((a, b) => (a.days_to_expiry ?? 999) - (b.days_to_expiry ?? 999));
        setRenewals(hot);
      }).catch(() => {});
    }
  }, [admin]);

  const licSub = licenses.reduce((s, l) => s + (l.license_type === 'subscription' && typeof l.cost === 'number' ? l.cost : 0), 0);
  const licPer = licenses.reduce((s, l) => s + (l.license_type !== 'subscription' && typeof l.cost === 'number' ? l.cost : 0), 0);

  const fmt = (s) => (s ? formatDateTime(s) : null);

  if (err) return <p className="text-sm text-red-600">統計の取得に失敗しました。</p>;
  if (!stats) return <p className="text-sm text-zinc-400">読み込み中...</p>;

  return (
    <div className="space-y-8" data-testid="it-dashboard">
      <PageHeader testid="it-dashboard-header" title="ITダッシュボード" description="申請・期限・容量の例外を優先して表示します" />

      {renewals.length > 0 && (
        <button
          type="button"
          onClick={() => navigate('/it/licenses')}
          data-testid="license-renewal-alert"
          className="w-full text-left rounded-xl border border-amber-300 bg-amber-50 shadow-sm p-4 transition-all duration-200 hover:shadow-md hover:border-amber-400"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-amber-100 text-amber-600">
              <Key size={18} weight="duotone" />
            </span>
            <span className="text-sm font-bold text-zinc-900">ライセンス更新期限が近づいています</span>
            {renewals.some((l) => l.expired) && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200 text-[11px] font-semibold" data-testid="renewal-expired-badge">
                <Warning size={12} weight="bold" /> 期限切れ {renewals.filter((l) => l.expired).length}件
              </span>
            )}
            {renewals.some((l) => l.expiring_soon) && (
              <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 text-[11px] font-semibold" data-testid="renewal-soon-badge">
                30日以内 {renewals.filter((l) => l.expiring_soon).length}件
              </span>
            )}
            <span className="ml-auto text-[11px] text-amber-700 font-medium shrink-0">ライセンス管理へ →</span>
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
            {renewals.slice(0, 4).map((l) => (
              <span key={l.id} className="text-xs text-zinc-600" data-testid={`renewal-item-${l.id}`}>
                <span className="font-medium text-zinc-800">{l.name}</span>
                <span className={`ml-1 font-semibold ${l.expired ? 'text-rose-600' : 'text-amber-700'}`}>
                  {l.expired ? `${Math.abs(l.days_to_expiry)}日超過` : `あと${l.days_to_expiry}日`}
                </span>
                <span className="ml-1 text-zinc-400">（{(l.expiry_date || '').slice(0, 10)}）</span>
              </span>
            ))}
            {renewals.length > 4 && <span className="text-xs text-zinc-400">他 {renewals.length - 4}件</span>}
          </div>
        </button>
      )}

      {admin && omada?.last_sync && omada.agent_online === false && (
        <button
          type="button"
          onClick={() => navigate('/it/vlan')}
          data-testid="agent-offline-alert"
          className="w-full text-left rounded-xl border border-rose-300 bg-rose-50 shadow-sm p-4 transition-all duration-200 hover:shadow-md hover:border-rose-400"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-rose-100 text-rose-600">
              <WifiSlash size={18} weight="duotone" />
            </span>
            <span className="text-sm font-bold text-zinc-900">同期エージェントがオフラインの可能性</span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200 text-[11px] font-semibold" data-testid="agent-offline-since">
              <Warning size={12} weight="bold" /> 最終報告から {fmtSince(omada.minutes_since)} 経過
            </span>
            <span className="ml-auto text-[11px] text-rose-700 font-medium shrink-0">Omada同期状況へ →</span>
          </div>
          <p className="mt-1.5 text-xs text-zinc-600">ルーター側エージェント（GL-BE3600）の稼働・ネットワーク接続を確認してください。停止中はIP台帳・トポロジーが更新されません。</p>
        </button>
      )}

      {fullRacks.length > 0 && (
        <button
          type="button"
          onClick={() => navigate('/it/floormap')}
          data-testid="rack-capacity-alert"
          className="w-full text-left rounded-xl border border-rose-300 bg-rose-50 shadow-sm p-4 transition-all duration-200 hover:shadow-md hover:border-rose-400"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-rose-100 text-rose-600">
              <Rows size={18} weight="duotone" />
            </span>
            <span className="text-sm font-bold text-zinc-900">ラック容量が逼迫しています</span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200 text-[11px] font-semibold" data-testid="rack-capacity-count">
              <Warning size={12} weight="bold" /> 使用率90%以上 {fullRacks.length}台
            </span>
            <span className="ml-auto text-[11px] text-rose-700 font-medium shrink-0">フロアマップへ →</span>
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
            {fullRacks.slice(0, 4).map((r) => (
              <span key={r.id} className="text-xs text-zinc-600" data-testid={`rack-capacity-item-${r.id}`}>
                <span className="font-medium text-zinc-800">{r.name}</span>
                <span className={`ml-1 font-semibold ${r.free === 0 ? 'text-rose-600' : 'text-rose-500'}`}>
                  {r.free === 0 ? '満杯' : `空き${r.free}U`}
                </span>
                <span className="ml-1 text-zinc-400 font-mono">
                  {r.used}/{r.units}U{r.floormap_name ? `・${r.floormap_name}` : ''}
                </span>
              </span>
            ))}
            {fullRacks.length > 4 && <span className="text-xs text-zinc-400">他 {fullRacks.length - 4}台</span>}
          </div>
        </button>
      )}

      <DashboardZone title="対応が必要" hint="承認待ちの申請とアラートを最優先で表示します" testid="it-action-zone">
        {(stats.pending_requests || 0) === 0 && renewals.length === 0 && fullRacks.length === 0 ? (
          <AllClear message="承認待ちの申請・アラートはありません" testid="it-allclear" />
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <ActionTile icon={ClipboardText} label="承認待ち申請" hint="IP割当などの申請" value={stats.pending_requests} urgent
              onClick={() => navigate('/it/requests')} testid="stat-pending" />
            <ActionTile icon={Key} label="ライセンス更新" hint="期限切れ・30日以内" value={renewals.length}
              onClick={() => navigate('/it/licenses')} testid="stat-renewals" />
            <ActionTile icon={Rows} label="ラック逼迫" hint="使用率90%以上" value={fullRacks.length}
              onClick={() => navigate('/it/floormap')} testid="stat-racks" />
            <ActionTile icon={CircleDashed} label="空きIP" hint={`総数 ${stats.total_ips} / 割当済 ${stats.assigned_ips}`} value={stats.available_ips}
              onClick={() => navigate('/it/vlan')} testid="stat-available" />
          </div>
        )}
      </DashboardZone>

      <div className={`grid grid-cols-1 gap-4 ${admin ? 'lg:grid-cols-5' : ''}`}>
        <div className={`bg-white rounded-xl border border-zinc-200 shadow-sm p-6 ${admin ? 'lg:col-span-3' : ''}`}>
          <h2 className="text-base font-bold text-zinc-900 mb-5">VLAN別 IP使用状況</h2>
          <div className="space-y-4">
            {stats.per_vlan.map((v) => {
              const pct = v.total ? Math.round((v.assigned / v.total) * 100) : 0;
              const warn = pct >= 90;
              return (
                <div key={v.key} data-testid={`vlan-usage-${v.key}`}>
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="font-medium text-zinc-800 truncate pr-2">{v.name}</span>
                    <span className="text-zinc-500 tabular-nums shrink-0">
                      {v.assigned} / {v.total}
                      <span className={`ml-1.5 font-semibold ${warn ? 'text-rose-600' : 'text-teal-700'}`}>{pct}%</span>
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-zinc-100 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${warn ? 'bg-rose-500' : 'bg-teal-700'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {admin && (
          <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-6" data-testid="omada-status-card">
            <div className="flex items-center justify-between gap-2 mb-4">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-indigo-100 text-indigo-600">
                  <WifiHigh size={18} weight="duotone" />
                </span>
                <h2 className="text-base font-bold text-zinc-900">Omada同期</h2>
              </div>
              {omada && omada.last_sync ? (
                omada.agent_online ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-medium" data-testid="omada-agent-online">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 ring-4 ring-emerald-500/20" /> 同期済み
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200 text-xs font-medium" data-testid="omada-agent-offline" title={`最終報告から ${fmtSince(omada.minutes_since)} 経過`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-rose-500 ring-4 ring-rose-500/20" /> オフライン（{fmtSince(omada.minutes_since)}）
                  </span>
                )
              ) : (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-zinc-100 text-zinc-500 border border-zinc-200 text-xs font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-zinc-400" /> 未同期
                </span>
              )}
            </div>
            {omada && omada.last_sync ? (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-500">最終同期</span>
                  <span className="font-medium text-zinc-900">{fmt(omada.last_sync.synced_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Omada由来のIP</span>
                  <span className="font-medium text-zinc-900 tabular-nums">{omada.omada_synced_ips} 件</span>
                </div>
                <div className="text-xs text-zinc-400 pt-1 border-t border-zinc-200">
                  受信 {omada.last_sync.received} · 一致 {omada.last_sync.matched} · 新規 {omada.last_sync.created}
                  {omada.last_sync.released_stale > 0 && (
                    <span className="text-amber-600 font-semibold" data-testid="omada-released-stale"> · 自動解放 {omada.last_sync.released_stale}</span>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-zinc-500 leading-relaxed">
                まだ同期されていません。ローカル同期エージェント（GL-BE3600 等）を設定すると、Omadaの接続クライアントが自動反映されます。
              </p>
            )}
          </div>

          {/* ネットワークヘルス — 有線/無線数 + トップトーカー */}
          <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-6" data-testid="network-health-card">
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-emerald-100 text-emerald-600">
                <Pulse size={18} weight="duotone" />
              </span>
              <h2 className="text-base font-bold text-zinc-900">ネットワークヘルス</h2>
            </div>
            {health && health.total > 0 ? (
              <>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="rounded-lg bg-zinc-50 border border-zinc-100 p-2 text-center" data-testid="health-wired">
                    <p className="text-lg font-bold text-zinc-800 tabular-nums flex items-center justify-center gap-1"><Plugs size={14} className="text-blue-500" />{health.wired}</p>
                    <p className="text-[10px] text-zinc-500">有線</p>
                  </div>
                  <div className="rounded-lg bg-zinc-50 border border-zinc-100 p-2 text-center" data-testid="health-wireless">
                    <p className="text-lg font-bold text-zinc-800 tabular-nums flex items-center justify-center gap-1"><WifiHigh size={14} className="text-emerald-500" />{health.wireless}</p>
                    <p className="text-[10px] text-zinc-500">無線</p>
                  </div>
                  <div className="rounded-lg bg-zinc-50 border border-zinc-100 p-2 text-center" data-testid="health-total">
                    <p className="text-lg font-bold text-zinc-800 tabular-nums">{health.total}</p>
                    <p className="text-[10px] text-zinc-500">クライアント合計</p>
                  </div>
                </div>
                {health.has_traffic ? (
                  <div className="space-y-2" data-testid="health-top-talkers">
                    <p className="text-[11px] font-semibold text-zinc-500">トップトーカー（累計通信量）</p>
                    {health.top_talkers.map((t) => (
                      <div key={t.mac || t.ip} data-testid={`health-talker-${t.mac}`}>
                        <div className="flex items-center justify-between text-xs mb-0.5">
                          <span className="font-medium text-zinc-700 truncate pr-2">{t.name}<span className="ml-1.5 font-mono text-[9px] text-zinc-400">{t.ip}</span></span>
                          <span className="shrink-0 font-mono text-[10px] text-zinc-500 tabular-nums">
                            <ArrowUp size={9} className="inline text-sky-500" />{fmtBytes(t.traffic_up)}
                            <ArrowDown size={9} className="inline text-emerald-500 ml-1.5" />{fmtBytes(t.traffic_down)}
                          </span>
                        </div>
                        <div className="h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                          <div className="h-full rounded-full bg-teal-700"
                            style={{ width: `${Math.max(4, Math.round((t.total / (health.top_talkers[0]?.total || 1)) * 100))}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-zinc-400" data-testid="health-no-traffic">通信量データ未同期 — エージェント更新後の同期で表示されます。</p>
                )}
                {health.ap_load && health.ap_load.length > 0 && (
                  <div className="space-y-2 mt-4 pt-3 border-t border-zinc-200" data-testid="health-ap-load">
                    <p className="text-[11px] font-semibold text-zinc-500">Wi-Fi 負荷（AP別クライアント数）</p>
                    {health.ap_load.map((ap) => {
                      const lv = ap.clients >= 30 ? 'rose' : ap.clients >= 20 ? 'amber' : 'emerald';
                      const maxC = Math.max(health.ap_load[0]?.clients || 1, 1);
                      return (
                        <div key={ap.mac} data-testid={`health-ap-${ap.mac}`}>
                          <div className="flex items-center justify-between text-xs mb-0.5">
                            <span className="font-medium text-zinc-700 truncate pr-2">
                              {ap.name}
                              {ap.ssids.length > 0 && <span className="ml-1.5 text-[9px] text-zinc-400">{ap.ssids.join(' / ')}</span>}
                            </span>
                            <span className={`shrink-0 inline-flex items-center gap-1 px-1.5 py-0 rounded-full text-[10px] font-bold tabular-nums ${
                              lv === 'rose' ? 'bg-rose-100 text-rose-700' : lv === 'amber' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                            }`} data-testid={`health-ap-count-${ap.mac}`}>
                              {ap.clients}台{lv === 'rose' ? '・過密' : lv === 'amber' ? '・混雑' : ''}
                            </span>
                          </div>
                          <div className="h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                            <div className={`h-full rounded-full ${lv === 'rose' ? 'bg-rose-500' : lv === 'amber' ? 'bg-amber-500' : 'bg-emerald-500'}`}
                              style={{ width: `${Math.max(4, Math.round((ap.clients / maxC) * 100))}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-zinc-500 leading-relaxed" data-testid="health-empty">
                トポロジー未同期です。エージェントの次回同期後にクライアント数とトップトーカーが表示されます。
              </p>
            )}
            {healthHist.length >= 2 && (() => {
              const histData = healthHist.map((h) => ({
                date: h.date.slice(5).replace('-', '/'), 有線: h.wired, 無線: h.wireless,
              }));
              const apMax = {};
              healthHist.forEach((h) => (h.ap_load || []).forEach((a) => {
                if (!apMax[a.mac] || a.clients > apMax[a.mac].max) apMax[a.mac] = { name: a.name, max: a.clients };
              }));
              const topAps = Object.entries(apMax).sort((a, b) => b[1].max - a[1].max).slice(0, 4);
              const apData = healthHist.map((h) => {
                const row = { date: h.date.slice(5).replace('-', '/') };
                topAps.forEach(([mac, meta]) => {
                  row[meta.name] = (h.ap_load || []).find((a) => a.mac === mac)?.clients ?? 0;
                });
                return row;
              });
              const apColors = ['#0052FF', '#10b981', '#f59e0b', '#f43f5e'];
              return (
                <div className="mt-4 pt-3 border-t border-zinc-200 space-y-3" data-testid="health-history">
                  <p className="text-[11px] font-semibold text-zinc-500">推移（日次ピーク・過去{healthHist.length}日）</p>
                  <div className="h-32" data-testid="health-history-clients">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={histData} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                        <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#a1a1aa' }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                        <YAxis tick={{ fontSize: 9, fill: '#a1a1aa' }} tickLine={false} axisLine={false} allowDecimals={false} />
                        <ChartTooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                        <Area type="monotone" dataKey="有線" stackId="1" stroke="#0052FF" fill="#0052FF" fillOpacity={0.25} strokeWidth={1.5} />
                        <Area type="monotone" dataKey="無線" stackId="1" stroke="#10b981" fill="#10b981" fillOpacity={0.25} strokeWidth={1.5} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  {topAps.length > 0 && (
                    <>
                      <p className="text-[10px] text-zinc-400">AP別クライアント数の推移</p>
                      <div className="h-28" data-testid="health-history-aps">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={apData} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                            <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#a1a1aa' }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                            <YAxis tick={{ fontSize: 9, fill: '#a1a1aa' }} tickLine={false} axisLine={false} allowDecimals={false} />
                            <ChartTooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                            {topAps.map(([mac, meta], i) => (
                              <Line key={mac} type="monotone" dataKey={meta.name} stroke={apColors[i]} strokeWidth={1.5} dot={false} />
                            ))}
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
          </div>
          </div>
        )}
      </div>
      {admin && (licSub > 0 || licPer > 0 || (assetStats && (assetStats.total_cost > 0 || assetStats.total_book_value > 0))) && (
        <MetricsDisclosure title="ITコストサマリー（参考指標）" testid="it-cost-summary">
          <div className="flex items-center gap-2 mb-3 pt-2">
            <CurrencyJpy size={16} weight="duotone" className="text-zinc-400" />
            <p className="text-xs text-zinc-500">タイルをクリックすると内訳（ベンダー別・月別推移・コストレポート印刷）へ移動します。</p>
            <button type="button" data-testid="it-budget-report-btn"
              onClick={() => openPrintWindow(buildItBudgetReportHtml(licenses, assets))}
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-zinc-200 bg-white text-zinc-700 text-xs font-medium hover:bg-zinc-50 transition-colors">
              <Printer size={14} weight="duotone" /> IT予算レポート
            </button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <button type="button" onClick={() => navigate('/it/licenses')} data-testid="cost-tile-lic-sub"
              className="text-left rounded-lg border border-zinc-200 p-3 hover:border-teal-300 transition-colors">
              <p className="text-[10px] text-zinc-500">ライセンス サブスク（年間）</p>
              <p className="text-lg font-bold text-zinc-900 mt-0.5">{yenFmt(licSub)}</p>
            </button>
            <button type="button" onClick={() => navigate('/it/licenses')} data-testid="cost-tile-lic-per"
              className="text-left rounded-lg border border-zinc-200 p-3 hover:border-teal-300 transition-colors">
              <p className="text-[10px] text-zinc-500">ライセンス 買い切り</p>
              <p className="text-lg font-bold text-zinc-900 mt-0.5">{yenFmt(licPer)}</p>
            </button>
            <button type="button" onClick={() => navigate('/it/assets')} data-testid="cost-tile-asset-cost"
              className="text-left rounded-lg border border-zinc-200 p-3 hover:border-teal-300 transition-colors">
              <p className="text-[10px] text-zinc-500">資産 取得価額合計</p>
              <p className="text-lg font-bold text-zinc-900 mt-0.5">{yenFmt(assetStats?.total_cost)}</p>
            </button>
            <button type="button" onClick={() => navigate('/it/assets')} data-testid="cost-tile-asset-book"
              className="text-left rounded-lg border border-zinc-200 p-3 hover:border-teal-300 transition-colors">
              <p className="text-[10px] text-zinc-500">資産 現在簿価合計</p>
              <p className="text-lg font-bold text-zinc-900 mt-0.5">{yenFmt(assetStats?.total_book_value)}</p>
            </button>
          </div>
        </MetricsDisclosure>
      )}

    </div>
  );
}
